import classNames from "classnames";
import Badge from "components/base/Badge";
import Button from "components/base/Button";
import { Col, Form, Row } from "react-bootstrap";
import { ToDoItem } from "data/project-management/todoListData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment, useCallback, useEffect, useState } from "react";
import { dateFormatter } from "helpers/utils";
import {
  faEdit,
  faTrash,
  faThumbsUp,
  faThumbsDown,
} from "@fortawesome/free-solid-svg-icons";

type Breakpoints = "md" | "lg" | "xl" | "xxl";

interface TodoListItemInterface {
  todo: ToDoItem;
  handleEdit?: any;
  className?: string;
  handleDelete?: any;
  setStatusType?: any;
  handleThumbsDown?: any;
  labelClassName?: string;
  onClick?: (item: ToDoItem) => void;
  halfLayoutBreakpoints?: Breakpoints[];
  fullLayoutBreakpoints?: Breakpoints[];
}
type FullLayoutBreakpoints = {
  [key in Breakpoints]?: "auto";
};
type HalfLayoutBreakpoints = {
  [key in Breakpoints]?: 12;
};

const TodoListItem = ({
  todo,
  onClick,
  className,
  handleEdit,
  handleDelete,
  labelClassName,
  handleThumbsDown,
  halfLayoutBreakpoints = [],
  fullLayoutBreakpoints = [],
}: TodoListItemInterface) => {
  const [selected, setSelected] = useState<any>(todo.is_completed);

  const handleSelectionChange = (todo: any) => {
    if (onClick) {
      onClick(todo);
      setSelected(!selected);
    } else {
      setSelected(!selected);
    }
  };

  const handleClick = (item: ToDoItem) => {
    if (onClick) {
      onClick(item);
      setSelected(!selected);
    } else {
      setSelected(!selected);
    }
  };

  const getBreakpointClasses = useCallback(
    (className: string, values: (number | string)[]) =>
      [
        ...halfLayoutBreakpoints.map(
          (breakpoint) => `${className}-${breakpoint}-${values[0]}`
        ),
        ...fullLayoutBreakpoints.map(
          (breakpoint) => `${className}-${breakpoint}-${values[1]}`
        ),
      ].join(" "),
    [halfLayoutBreakpoints, fullLayoutBreakpoints]
  );

  useEffect(() => {
    setSelected(todo.is_completed);
  }, [todo]);

  return (
    <Fragment>
      <div
        className={classNames(
          className,
          "d-flex align-items-center hover-actions-trigger border-bottom border-translucent gap-2 todolist-item"
        )}
      >
        <Form.Check.Input
          type="checkbox"
          checked={selected}
          className={classNames("flex-shrink-0 my-0 align-self-start")}
          onChange={() => handleSelectionChange(todo)}
        />
        <Row className="justify-content-between align-items-center btn-reveal-trigger border-translucent gx-0 flex-1 gy-1">
          <Col
            xs={12}
            {...fullLayoutBreakpoints?.reduce(
              (acc: FullLayoutBreakpoints, val: Breakpoints) => {
                acc[val] = "auto";
                return acc;
              },
              {}
            )}
            {...halfLayoutBreakpoints?.reduce(
              (acc: HalfLayoutBreakpoints, val: Breakpoints) => {
                acc[val] = 12;
                return acc;
              },
              {}
            )}
          >
            <div className="d-flex align-items-center lh-1 gap-2">
              <div>
                <h5
                  className={classNames(
                    labelClassName,
                    "mb-0 fw-semibold text-body-tertiary cursor-pointer pb-1",
                    {
                      "text-decoration-line-through text-body-quaternary":
                        selected,
                    }
                  )}
                  onClick={() => handleClick(todo)}
                >
                  {todo.task}
                </h5>
                <div className="d-flex gap-2 align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    {/* {compareDateWithToday(todo.due_date) === "Due" && (
                      <Badge
                        variant="phoenix"
                        bg="danger"
                        className="fs-10 ms-auto"
                      >
                        Expired
                      </Badge>
                    )} */}

                    {todo.status && (
                      <Badge
                        variant="phoenix"
                        bg="primary"
                        className="fs-10 ms-auto"
                      >
                        {todo.status}
                      </Badge>
                    )}
                    {todo.priority && (
                      <Badge
                        variant="phoenix"
                        bg="warning"
                        className="fs-10 ms-auto"
                      >
                        {todo.priority}
                      </Badge>
                    )}
                  </div>
                  {todo.due_date && (
                    <p
                      className={classNames(
                        getBreakpointClasses("me", [2, 3]),
                        "text-body-tertiary fs-10 me-2 mb-0"
                      )}
                    >
                      {dateFormatter(todo.due_date, false)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <div
          className={classNames(
            getBreakpointClasses("d", ["none", "block"]),
            "d-none end-0 position-absolute"
          )}
          style={{ top: "23%" }}
        >
          <div className="hover-actions end-0">
            <Button
              variant="phoenix-secondary"
              className="btn-icon fs-10 me-1"
              onClick={handleEdit}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>

            <Button
              variant="phoenix-secondary"
              className="btn-icon fs-10 me-1"
              onClick={handleDelete}
            >
              <FontAwesomeIcon icon={faTrash} className="text-danger" />
            </Button>
            <Button
              variant="phoenix-secondary"
              className="btn-icon fs-9"
              onClick={handleThumbsDown}
            >
              <FontAwesomeIcon
                icon={todo?.is_liked ? faThumbsDown : faThumbsUp}
                className={todo?.is_liked ? "text-danger" : "text-success"}
              />
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default TodoListItem;
